import { MarkerType } from "reactflow";
import { stratify, tree } from "d3-hierarchy";
import {
    actionOptions,
    bulkActionOperatorOptions,
    conditionActionOperatorOptions,
    getActionOptions,
} from "../components/MainContent/SMRSettings/AutomationEngine/RuleEngineComponents/RuleEngineUtils";

export const getRandomId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const getInitialNodes = () => {
    return {
        root: {
            id: "root",
            position: { x: 0, y: 0 },
            type: "rootNode",
            data: {
                actionEvents: [],
                childNodes: ["add-root"],
                parentId: null,
                childEdges: ["e1-default"],
            },
        },
        "add-root": {
            id: "add-root",
            position: { x: 0, y: 100 },
            type: "addNode",
            data: {
                actionEvents: [],
                childNodes: [],
                parentId: "root",
                childEdges: [],
            },
        },
    };
};

export const getInitialEdges = () => {
    return {
        "e1-default": {
            id: "e1-default",
            source: "root",
            target: "add-root",
            // animated: true,
            // type: "custom-boolean",
            markerEnd: {
                type: MarkerType.ArrowClosed,
            },

            // data: {
            //     label: "edge label",
            // },
        },
    };
};

export const formatNodesAndEdges = ({ nodes, edges, events, name }) => {
    const nodesObj = JSON.parse(JSON.stringify(nodes));
    const edgesObj = JSON.parse(JSON.stringify(edges));

    let updatedCurrentNode = JSON.parse(JSON.stringify(events.node));
    const nodeType = events.nodeActionEvent.value === "condition_action" ? "conditionalNode" : "bulkActionNode";
    const count = events.nodeActionEvent.value === "condition_action" ? 2 : 1;

    // if (events.nodeActionEvent.value === "condition_action") {
    // then  make current node as conditional node, add 2 new child nodes
    // add 2 new edges make edge type as custom and add data.label

    const [newNodes, newEdges] = createNewNodesAndEdges({
        count,
        referenceNode: updatedCurrentNode,
        actionEvent: events.nodeActionEvent.value,
    });

    updatedCurrentNode = {
        ...updatedCurrentNode,
        type: nodeType,
        data: {
            ...updatedCurrentNode.data,
            actionEvents: events.actionFields,
            childNodes: Object.keys(newNodes),
            childEdges: Object.keys(newEdges),
            name, // box name
        },
    };

    return {
        nodes: {
            ...nodesObj,
            [updatedCurrentNode.id]: updatedCurrentNode,
            ...newNodes,
        },
        edges: {
            ...edgesObj,
            ...newEdges,
        },
    };
    // }
};

export const createNewNodesAndEdges = ({ count = 1, referenceNode, actionEvent }) => {
    const nodeObj = {};
    const edgeObj = {};

    let referenceX = referenceNode.position.x;
    for (let i = 0; i < count; i++) {
        let x = referenceX;
        let edgeType = null;
        let edgeLabel = null;

        if (actionEvent === "bulk_action") {
            // x = referenceX;
            edgeType = "default";
        } else if (actionEvent === "condition_action") {
            edgeType = "custom-boolean";
            if (i === 0) {
                x = referenceX + 100;
                edgeLabel = "Yes";
            } else {
                x = referenceX - 100;
                edgeLabel = "No";
            }
        }

        const nodeId = getRandomId();

        nodeObj[nodeId] = {
            id: nodeId,
            position: {
                x,
                y: referenceNode.position.y + 100,
            },
            type: "addNode",
            data: {
                actionEvents: [],
                childNodes: [],
                childEdges: [],
                parentId: referenceNode.id,
            },
        };
        const edgeId = getRandomId();

        edgeObj[edgeId] = {
            id: edgeId,
            source: referenceNode.id,
            target: nodeId,
            type: edgeType,
            markerEnd: {
                type: MarkerType.ArrowClosed,
            },

            data: {
                label: edgeLabel,
            },
        };
    }
    return [nodeObj, edgeObj];
};

export const getLayoutedElements = (nodesObj, edgesObj) => {
    const g = tree();

    let nodes = Object.values(nodesObj);
    let edges = Object.values(edgesObj);
    if (nodes.length === 0) return { nodesObj, edges };

    const hierarchy = stratify()
        .id((node) => node.id)
        .parentId((node) => edges.find((edge) => edge.target === node.id)?.source);
    const root = hierarchy(nodes);
    const layout = g.nodeSize([140, 160])(root);

    let updatedNodes = layout.descendants().map((node) => ({ ...node.data, position: { x: node.x, y: node.y } }));
    const updatedNodeObj = {};
    updatedNodes.forEach((obj) => {
        updatedNodeObj[obj.id] = obj;
    });

    return {
        nodes: updatedNodeObj,
        edges: edgesObj,
    };
};
export const updateActionEvents = ({ nodes, events, name }) => {
    let updatedCurrentNode = JSON.parse(JSON.stringify(events.node));

    updatedCurrentNode = {
        ...updatedCurrentNode,

        data: {
            ...updatedCurrentNode.data,
            actionEvents: events.actionFields,
            name, // box name
            isError: [],
        },
    };
    return {
        nodes: {
            ...nodes,
            [updatedCurrentNode.id]: updatedCurrentNode,
        },
    };
};

export const deleteNodesAndEdges = ({ nodes, edges, nodeId }) => {
    const referenceNode = nodes[nodeId];
    deleteChildren(nodes, nodeId, edges);
    return {
        nodes: {
            ...nodes,
            [referenceNode.id]: {
                ...referenceNode,
                data: {
                    ...referenceNode.data,
                    childNodes: [],
                    childEdges: [],
                    actionEvents: [],
                },
                type: "addNode",
            },
        },
        edges,
    };
};
const deleteChildren = (nodes, nodeToDelete, edges) => {
    if (nodes[nodeToDelete]) {
        const childNodes = nodes[nodeToDelete].data?.childNodes;
        const childEdges = nodes[nodeToDelete].data?.childEdges;

        for (const childNode of childNodes) {
            deleteChildren(nodes, childNode, edges);
        }
        delete nodes[nodeToDelete];

        for (const chidEdge of childEdges) delete edges[chidEdge];
    }
};
export const convertToRawData = ({ nodes, edges }) => {
    const rawData = {};

    const n = nodes;

    for (const nodeId in nodes) {
        const node = nodes[nodeId];
        const connectionData = [];
        const configData = [];
        const nodeData = JSON.parse(JSON.stringify(node.data));
        delete nodeData.actionEvents;

        if (node.data?.childNodes) {
            node.data.childNodes?.forEach((childNodeId) => {
                connectionData.push({ name: "", targetElementId: childNodeId });
            });
        }
        if (node.data?.actionEvents) {
            node.data?.actionEvents?.forEach((event) => {
                const eventobj = {
                    type: event.action.value,
                    name: event.action.value,
                    label: event.action.value,
                    fieldType: event.valueType,
                    checkCondition: [],
                    values: [],
                };
                // eventobj.fieldType = node.type === "conditionalNode" ? "PICKLIST_MULTISELECT" : "PICKLIST_MULTISELECT";
                if (event.operator?.value) eventobj.checkCondition = [event.operator?.value];
                // if(event.value?.value)
                if (node.type === "conditionalNode") {
                    if (Array.isArray(event.value)) {
                        event.value?.forEach((val) => {
                            eventobj.values.push({
                                key: val.value,
                                label: val.label,
                            });
                        });
                    } else {
                        eventobj.values.push({
                            key: event.value.value,
                            label: event.value.label,
                        });
                    }
                } else {
                    if (event.valueType === "inputField") {
                        if (event.action.value === "send_auto_response")
                            eventobj.values.push({
                                key: event.value,
                                label: event.value,
                                reply_mode: event.replyMode || "DM",
                            });
                        else
                            eventobj.values.push({
                                key: event.value?.value,
                                label: event.value?.value,
                            });
                    } else if (event.value?.value) {
                        eventobj.values.push({
                            key: event.value?.value,
                            label: event.value?.label,
                        });
                    }
                }

                configData.push(eventobj);
            });
        }

        rawData[nodeId] = {
            id: nodeId,
            connections: connectionData,
            additional: {
                position: node.position,
                // data: node.data,
                data: nodeData,
            },
            workflowElementType: node.type,
            content: {
                id: nodeId,
                type: node.type,
                name: "",
                description: "",
                config: configData,
            },
        };
    }
    return {
        formattedNodes: rawData,
        formattedEdges: edges,
    };
};
export const convertRawData = (ruleData) => {
    const nodes = {};
    const edges = ruleData.edges;
    delete ruleData.edges;

    // console.log("ruleData", ruleData);

    for (const nodeId in ruleData) {
        const node = ruleData[nodeId];

        const nodeData = node.additional;
        const isError = [];
        const errorValue = [];

        const actionEventLists = node.content.config.map((obj, id) => {
            const action = actionOptions.filter((action) => action.value === obj.type)?.[0] || {};
            const operatorType = node.workflowElementType === "conditionalNode" ? conditionActionOperatorOptions : bulkActionOperatorOptions;
            const operator = operatorType.filter((operator) => operator.value === obj.checkCondition[0])?.[0] || {};
            const OptionLists = getActionOptions(obj.type);
            let value = [];
            let replyMode = null;

            if (obj.fieldType === "inputField") value = obj.values?.[0]?.key;
            if (obj.type === "send_auto_response") replyMode = obj.values?.[0]?.reply_mode || "DM";
            else if (OptionLists.length > 0) {
                obj.values.forEach((indVal) => {
                    const val = OptionLists.find((option) => option.value === indVal.key);

                    if (val && val.value) value.push(val);
                    else {
                        errorValue.push(indVal);
                    }
                });
            }

            // if (!node.workflowElementType === "conditionalNode") {
            //     value = value[0];
            // }

            if (obj.values?.length > 0 && obj.fieldType !== "inputField") {
                if (!value || value?.length === 0 || obj.values?.length !== value?.length) {
                    // console.log("valaction.valueue", action.value, obj, value, errorValue);
                    isError.push({
                        action: obj.type,
                        rawValue: obj.values,
                        errorValue,
                    });
                }
            }

            return {
                id,
                action,
                operator,
                valueType: obj.fieldType,
                value: ["inputField", "multiField"].includes(obj.fieldType) ? value : value[0], // node.workflowElementType === "conditionalNode" ? value : value[0],
                replyMode,
            };
        });

        const position = nodeData.position;

        nodes[nodeId] = {
            id: nodeId,
            type: node.workflowElementType,
            position,
            data: {
                ...nodeData.data,
                actionEvents: actionEventLists,
                isError,
            },
        };
    }

    return { nodes, edges };
};
